import React, { useState, useMemo } from 'react';
import { useLang } from '../../i18n';

/**
 * Generic sortable audit table shared by every History view (Today + the standing
 * checklists), so they all read & sort the same way.
 *
 *   columns = [{ key, label, cell(row), sortVal?(row), className? }]
 *
 * Click a header to sort (toggles asc ⇄ desc). Filtering stays in the parent — this
 * component only sorts and renders. `onRemove(row)` adds a trailing ✕ column, and
 * `renderDetail(row)` adds a leading ▸ toggle that expands a full-width detail row
 * (`detailLabel` names that toggle; defaults to "Show every order").
 */
export function AuditTable({ columns, rows, defaultSort, rowKey, rowClass, onRemove, renderDetail, detailLabel }) {
  const { t } = useLang();
  const detailsLbl = detailLabel || t('table.details');
  const [sort, setSort] = useState(() => defaultSort || { key: columns[0].key, dir: 'asc' });
  const [open, setOpen] = useState(() => new Set());
  const toggleOpen = k => setOpen(prev => {
    const next = new Set(prev);
    if (!next.delete(k)) next.add(k);
    return next;
  });
  const span = columns.length + (renderDetail ? 1 : 0) + (onRemove ? 1 : 0);
  const onSort = k => setSort(s => (s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' }));
  const sorted = useMemo(() => {
    const col = columns.find(c => c.key === sort.key) || columns[0];
    const val = col.sortVal || (r => (r[col.key] ?? ''));
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => String(val(a)).localeCompare(String(val(b)), undefined, { numeric: true }) * dir);
  }, [rows, sort, columns]);

  return (
    <div className="do-htable-wrap">
      <table className="do-htable">
        <thead>
          <tr>
            {renderDetail && <th className="do-htable__expand" aria-label={detailsLbl} />}
            {columns.map(c => (
              <th key={c.key}>
                <button className="do-htable__sort" onClick={() => onSort(c.key)} aria-label={t('table.sortBy', { label: c.label })}>
                  {c.label}{sort.key === c.key && <span className="do-htable__arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </th>
            ))}
            {onRemove && <th aria-label={t('table.actions')} />}
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => {
            const key = rowKey(r);
            const expanded = !!renderDetail && open.has(key);
            return (
              <React.Fragment key={key}>
                <tr className={rowClass ? rowClass(r) : undefined}>
                  {renderDetail && (
                    <td className="do-htable__expand">
                      <button className="do-htable__toggle" onClick={() => toggleOpen(key)} aria-expanded={expanded}
                        aria-label={detailsLbl} title={detailsLbl}>{expanded ? '▾' : '▸'}</button>
                    </td>
                  )}
                  {columns.map(c => <td key={c.key} className={c.className}>{c.cell(r)}</td>)}
                  {onRemove && (
                    <td className="do-htable__actions">
                      <button className="do-htable__del" onClick={() => onRemove(r)}
                        aria-label={t('table.remove', { name: r.name || t('table.entry') })} title={t('common.remove')}>✕</button>
                    </td>
                  )}
                </tr>
                {expanded && (
                  <tr className="do-htable__detail-row">
                    <td colSpan={span}>{renderDetail(r)}</td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
